import { Camera, Droplet, Dumbbell, Flame, Trophy, Sparkles } from "lucide-react";
import Reveal from "@/components/Reveal";
import NomCharacter from "@/components/NomCharacter";

function Tag({ children }) {
  return (
    <span className="rounded-full border border-nom-border bg-nom-cream px-3 py-1 text-xs font-extrabold text-nom-inkSoft">
      {children}
    </span>
  );
}

function CardShell({ className = "", children, testId }) {
  return (
    <div
      className={`h-full rounded-[28px] border border-nom-border bg-white p-7 shadow-nom transition-all duration-300 hover:-translate-y-1 hover:shadow-nomLg ${className}`}
      data-testid={testId}
    >
      {children}
    </div>
  );
}

export default function NomNomSection() {
  return (
    <section id="nomnom" className="scroll-mt-24 py-24 sm:py-28" data-testid="nomnom-section">
      <div className="mx-auto max-w-6xl px-6">
        <Reveal>
          <div className="flex flex-wrap items-center gap-3">
            <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-nom-coral" data-testid="nomnom-eyebrow">
              Our first app
            </p>
            <span
              className="inline-flex items-center gap-1.5 rounded-full bg-nom-sunny px-3 py-1 text-[11px] font-extrabold uppercase tracking-widest text-nom-ink"
              data-testid="nomnom-status-badge"
            >
              <Sparkles className="h-3 w-3" aria-hidden="true" />
              Available now
            </span>
          </div>
          <h2 className="mt-4 font-display text-3xl font-semibold tracking-tight text-nom-ink sm:text-4xl">
            Meet NomNom
          </h2>
          <p className="mt-5 max-w-3xl text-base leading-relaxed text-nom-inkSoft sm:text-lg" data-testid="nomnom-intro">
            NomNom pairs practical nutrition and wellness tracking with a companion who reacts to how you're doing. Log
            meals, keep an eye on macros and hydration, move your body — and Nom celebrates with you.
          </p>
        </Reveal>

        <div className="mt-12 grid gap-6 md:grid-cols-12">
          <Reveal className="md:col-span-8">
            <CardShell testId="nomnom-card-scan" className="flex h-full flex-col overflow-hidden p-0">
              <div className="p-7 pb-0 sm:p-8 sm:pb-0">
                <h3 className="font-display text-2xl font-semibold text-nom-ink">Log a meal in seconds</h3>
                <p className="mt-2 max-w-lg text-sm leading-relaxed text-nom-inkSoft">
                  Point your camera at a plate and NomNom estimates the nutrition with AI — or log by search, barcode or
                  a quick description. Review, adjust, and feed Nom.
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Tag>AI photo scanning</Tag>
                  <Tag>Search & barcode</Tag>
                  <Tag>Saved meals</Tag>
                </div>
              </div>
              <div className="relative mt-6 flex-1 overflow-hidden rounded-t-[28px]">
                <img
                  src="https://images.unsplash.com/photo-1546069901-ba9599a7e63c?q=85&w=1400&auto=format&fit=crop"
                  alt="A vibrant, healthy salad bowl being scanned by NomNom"
                  className="h-64 w-full object-cover sm:h-72"
                  loading="lazy"
                />
                <div className="absolute inset-x-4 bottom-4 rounded-3xl border border-white/50 bg-white/70 p-4 backdrop-blur-xl">
                  <p className="text-xs font-extrabold uppercase tracking-widest text-nom-inkSoft">Scanning your meal…</p>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <span className="rounded-full bg-nom-peach px-2.5 py-1 text-[11px] font-extrabold text-nom-ink">Estimated calories</span>
                    <span className="rounded-full bg-nom-peach px-2.5 py-1 text-[11px] font-extrabold text-nom-ink">Protein</span>
                    <span className="rounded-full bg-nom-peach px-2.5 py-1 text-[11px] font-extrabold text-nom-ink">Carbs</span>
                    <span className="rounded-full bg-nom-peach px-2.5 py-1 text-[11px] font-extrabold text-nom-ink">Fat</span>
                  </div>
                  <span className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-nom-coral px-4 py-2 text-xs font-extrabold text-white">
                    <Camera className="h-3.5 w-3.5" aria-hidden="true" /> Feed NomNom
                  </span>
                </div>
              </div>
            </CardShell>
          </Reveal>

          <Reveal delay={0.1} className="md:col-span-4">
            <CardShell testId="nomnom-card-nom">
              <h3 className="font-display text-2xl font-semibold text-nom-ink">Meet Nom</h3>
              <div className="dot-grid my-5 flex items-center justify-center rounded-3xl bg-nom-peach/70 py-6">
                <NomCharacter expression="joyful" className="h-28 w-28" />
              </div>
              <p className="text-sm leading-relaxed text-nom-inkSoft">
                A virtual companion that reacts to your progress. Feed Nom well and Nom glows — with levels, streaks and
                outfits to collect along the way.
              </p>
            </CardShell>
          </Reveal>

          <Reveal className="md:col-span-4">
            <CardShell testId="nomnom-card-macros">
              <h3 className="font-display text-2xl font-semibold text-nom-ink">Know your numbers</h3>
              <p className="mt-2 text-sm leading-relaxed text-nom-inkSoft">
                Personalized daily targets for calories, protein, carbs and fat — plus fiber — estimated from your goals.
              </p>
              <div className="mt-5 flex items-end justify-center gap-4">
                {[
                  { label: "Cal.", pct: 0.7, color: "#FF7369" },
                  { label: "Pro.", pct: 0.55, color: "#7C5CFF" },
                  { label: "Carbs", pct: 0.4, color: "#F2A93B" },
                  { label: "Fat", pct: 0.25, color: "#2EC4B6" },
                ].map((r) => (
                  <svg key={r.label} viewBox="0 0 44 44" className="h-14 w-14 -rotate-90">
                    <circle cx="22" cy="22" r="17" fill="none" stroke="#F3E4DA" strokeWidth="7" />
                    <circle
                      cx="22"
                      cy="22"
                      r="17"
                      fill="none"
                      stroke={r.color}
                      strokeWidth="7"
                      strokeLinecap="round"
                      strokeDasharray={2 * Math.PI * 17}
                      strokeDashoffset={2 * Math.PI * 17 * (1 - r.pct)}
                    />
                  </svg>
                ))}
              </div>
            </CardShell>
          </Reveal>

          <Reveal delay={0.08} className="md:col-span-4">
            <CardShell testId="nomnom-card-hydration">
              <h3 className="font-display text-2xl font-semibold text-nom-ink">Stay hydrated</h3>
              <p className="mt-2 text-sm leading-relaxed text-nom-inkSoft">
                Gentle hydration tracking with a daily water goal — and Nom lets you know when it's time for a sip.
              </p>
              <div className="mt-5 flex items-center justify-center gap-4">
                <svg viewBox="0 0 44 44" className="h-16 w-16 -rotate-90">
                  <circle cx="22" cy="22" r="17" fill="none" stroke="#F3E4DA" strokeWidth="7" />
                  <circle
                    cx="22"
                    cy="22"
                    r="17"
                    fill="none"
                    stroke="#3DA5F4"
                    strokeWidth="7"
                    strokeLinecap="round"
                    strokeDasharray={2 * Math.PI * 17}
                    strokeDashoffset={2 * Math.PI * 17 * 0.6}
                  />
                </svg>
                <Droplet className="h-6 w-6 text-nom-water" aria-hidden="true" />
              </div>
            </CardShell>
          </Reveal>

          <Reveal delay={0.16} className="md:col-span-4">
            <CardShell testId="nomnom-card-exercise">
              <h3 className="font-display text-2xl font-semibold text-nom-ink">Move more</h3>
              <p className="mt-2 text-sm leading-relaxed text-nom-inkSoft">
                Log workouts and see the effort reflected in your day — your body metrics stay yours alone.
              </p>
              <div className="mt-5 space-y-2.5">
                {[
                  { icon: Dumbbell, label: "Strength", min: "30 min" },
                  { icon: Flame, label: "Walk", min: "25 min" },
                ].map((row) => (
                  <div key={row.label} className="flex items-center justify-between rounded-2xl border border-nom-border bg-nom-cream px-4 py-2.5">
                    <span className="inline-flex items-center gap-2 text-sm font-extrabold text-nom-ink">
                      <row.icon className="h-4 w-4 text-nom-coral" aria-hidden="true" />
                      {row.label}
                    </span>
                    <span className="text-xs font-bold text-nom-muted">{row.min}</span>
                  </div>
                ))}
              </div>
            </CardShell>
          </Reveal>

          <Reveal className="md:col-span-12">
            <CardShell testId="nomnom-card-progress" className="grid items-center gap-8 md:grid-cols-2">
              <div>
                <h3 className="font-display text-2xl font-semibold text-nom-ink">Progress you can feel</h3>
                <p className="mt-3 text-sm leading-relaxed text-nom-inkSoft sm:text-base">
                  Weekly reports, achievements and streaks keep the habit alive. Add friends and cheer each other on
                  with high fives — your numbers stay private, your encouragement doesn't.
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-nom-sunny px-3 py-1.5 text-xs font-extrabold text-nom-ink">
                    <Trophy className="h-3.5 w-3.5" aria-hidden="true" /> Achievements
                  </span>
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-nom-border px-3 py-1.5 text-xs font-extrabold text-nom-inkSoft">
                    Weekly reports
                  </span>
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-nom-border px-3 py-1.5 text-xs font-extrabold text-nom-inkSoft">
                    Friend feed
                  </span>
                </div>
              </div>
              <div className="flex h-40 items-end justify-center gap-2.5 rounded-3xl bg-nom-peach/50 p-5 sm:h-48">
                {[35, 55, 45, 70, 60, 85, 100].map((h, i) => (
                  <div
                    key={i}
                    className={`w-7 rounded-t-xl ${i === 6 ? "bg-nom-coral" : "bg-nom-coral/35"}`}
                    style={{ height: `${h}%` }}
                  />
                ))}
              </div>
            </CardShell>
          </Reveal>
        </div>

        <Reveal>
          <p className="mt-8 text-center text-xs font-bold leading-relaxed text-nom-muted" data-testid="nomnom-disclaimer">
            NomNom: AI Calorie Tracker is available on the App Store and Google Play.
          </p>
        </Reveal>
      </div>
    </section>
  );
}
